// Sentry scrubbing for the login gauntlet (spec decision D6,
// docs/superpowers/specs/2026-09-01-native-app-analytics-and-consent-design.md).
//
// peikko is where phone numbers and email addresses are typed, by children, before
// anyone is authenticated. Sentry's terms are the binding constraint: "Customer must
// not use the Service with Sensitive Personal Information". That is a property of the
// PAYLOADS, not of the user model — and pre-auth there is no user model to gate on
// anyway, which is the same reason the analytics gate could not be applied here.
//
// Without these hooks Sentry's DEFAULTS decide what leaves: console output, fetch/XHR
// URLs, navigation URLs, and DOM click breadcrumbs. On this service a `ui.input`
// breadcrumb is somebody's phone number.
//
// Mirrors st-toosa/utils/sentry-privacy.ts. Duplicated rather than shared: services
// here have no direct imports between them, only API contracts.

type Breadcrumb = {
  category?: string;
  message?: string;
  data?: Record<string, unknown>;
  [key: string]: unknown;
};

type SentryRequest = {
  url?: string;
  data?: unknown;
  // Sentry carries the query as its OWN field as well as inside `url`. Stripping only
  // the url is cosmetic — this is the field a `?email=` or `?phone=` would travel in.
  query_string?: unknown;
  cookies?: unknown;
  env?: unknown;
  headers?: Record<string, string>;
  [key: string]: unknown;
};

type SentryEvent = {
  request?: SentryRequest;
  [key: string]: unknown;
};

export const stripQuery = (url?: string): string | undefined => {
  if (typeof url !== 'string') return url;
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
};

// A JWT is three base64url segments and starts `eyJ` (base64 of `{"`). peikko's
// activation flow puts one in a redirect URL along with a base64 user object.
const JWT = /eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g;

// A log message usually EMBEDS a URL rather than being one, so matching only
// whole-string URLs left `redirecting to https://x?token=...` untouched. Strip the
// query from any absolute URL wherever it appears.
const URL_WITH_QUERY = /(https?:\/\/[^\s]*?)[?#][^\s]*/g;

const sanitiseString = (v: string): string => {
  let out = v.replace(URL_WITH_QUERY, '$1');
  // A bare relative path as the entire value, e.g. a breadcrumb `url` field.
  if (out.startsWith('/')) out = stripQuery(out) as string;
  return out.replace(JWT, '[redacted-jwt]');
};

// Walks breadcrumb data and sanitises every string it finds, at any depth.
// Depth-capped so a cyclic or pathological object cannot hang the SDK's send path.
const sanitiseData = (value: unknown, depth = 0): unknown => {
  // FAIL CLOSED AT THE BOUNDARY TOO. Returning the value here would hand back an
  // unsanitised subtree — a JWT or a query URL nested five levels deep would go
  // straight out, defeating the whole point at exactly the place the guard exists.
  // Redacting also breaks any cycle rather than preserving it.
  if (depth > 4) return typeof value === 'string' ? sanitiseString(value) : '[redacted-depth]';
  if (typeof value === 'string') return sanitiseString(value);
  if (Array.isArray(value)) return value.map((v) => sanitiseData(v, depth + 1));
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        sanitiseData(v, depth + 1),
      ]),
    );
  }
  return value;
};

export const beforeBreadcrumb = (
  breadcrumb: Breadcrumb | null,
): Breadcrumb | null => {
  if (!breadcrumb) return breadcrumb;

  switch (breadcrumb.category) {
    // Unreviewed by construction, and this service logs through it during login.
    case 'console':
      return null;

    // `ui.input` on the login flow is a phone number or an email address, and
    // `ui.click` carries the element's text and aria-label.
    case 'ui.click':
    case 'ui.input':
      return null;

    // from/to are URLs, so they carry query strings.
    case 'navigation':
      return breadcrumb.data
        ? {
            ...breadcrumb,
            data: {
              ...breadcrumb.data,
              from: stripQuery(breadcrumb.data.from as string | undefined),
              to: stripQuery(breadcrumb.data.to as string | undefined),
            },
          }
        : breadcrumb;

    case 'fetch':
    case 'xhr':
      return breadcrumb.data
        ? {
            ...breadcrumb,
            data: {
              ...breadcrumb.data,
              url: stripQuery(breadcrumb.data.url as string | undefined),
            },
          }
        : breadcrumb;

    // FAIL CLOSED. This branch used to pass custom breadcrumbs through untouched,
    // which meant the single worst payload in this repo escaped: the activation
    // logger emits a `peikko.activation` breadcrumb whose data carries
    // `redirectUrl`, and that URL contains the JWT and a base64 user object.
    //
    // A scrubber whose default is "pass through" only protects the cases someone
    // thought of. Sanitise everything and let the known categories above be the
    // stricter exceptions, not the only protection.
    default:
      // `message` is a first-class breadcrumb field, not part of `data`, and was
      // previously untouched — so a breadcrumb carrying its content there escaped
      // even though the same string inside `data` would have been cleaned. Sanitise
      // both, and do it whether or not `data` is present.
      return {
        ...breadcrumb,
        ...(typeof breadcrumb.message === 'string'
          ? { message: sanitiseString(breadcrumb.message) }
          : {}),
        ...(breadcrumb.data
          ? { data: sanitiseData(breadcrumb.data) as Record<string, unknown> }
          : {}),
      };
  }
};

export const beforeSend = (event: SentryEvent | null): SentryEvent | null => {
  if (!event) return event;

  if (event.request) {
    const request: SentryRequest = { ...event.request };
    request.url = stripQuery(request.url);
    // A SEPARATE field from `url`; stripping the url alone leaves this intact.
    delete request.query_string;
    // Request bodies here carry the phone number, email and one-time password.
    delete request.data;
    delete request.cookies;
    delete request.env;
    if (request.headers) {
      const headers = { ...request.headers };
      delete headers.Cookie;
      delete headers.cookie;
      delete headers.Authorization;
      delete headers.authorization;
      delete headers['x-internal-secret'];
      delete headers['X-Internal-Secret'];
      // Referer is a URL and therefore carries a query string.
      if (headers.Referer) headers.Referer = stripQuery(headers.Referer) as string;
      if (headers.referer) headers.referer = stripQuery(headers.referer) as string;
      request.headers = headers;
    }
    event.request = request;
  }

  // Deliberately NOT guessing at "identifier-like" content in exception messages. A
  // heuristic would mangle real errors and still miss cases. The mitigation is
  // upstream; verify by triggering a real error mid-login on test and reading what
  // actually landed in Sentry.
  return event;
};
